import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Swal from 'sweetalert2';

const PartnerServices = () => {
  const navigate = useNavigate();
  const partner = AuthService.getCurrentpartner();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Debounced search: refetch 400ms after the user stops typing.
  // Runs on mount (search = '') and on every keystroke change.
  useEffect(() => {
    if (!partner) { navigate('/partner/login'); return; }
    const timer = setTimeout(() => { getServices(); }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const getServices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: 50, ...(search && { search }) }).toString();
      const res = await ApiService.request({ method: 'GET', url: `partner/getServices?${params}` });
      if (res.data.status) setServices(res.data.data || []);
      else toast.error(res.data.message);
    } catch { toast.error('Failed to fetch services'); }
    finally { setLoading(false); }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Service', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#d33',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await ApiService.request({ method: 'DELETE', url: `partner/deleteService/${id}` });
        if (res.data.status) { toast.success(res.data.message); getServices(); }
        else toast.error(res.data.message);
      } catch { toast.error('Error deleting service'); }
    });
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div className="app-container container-xxl d-flex flex-stack">
          <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
            Services
          </h1>
          <button
            className="btn btn-sm text-white"
            style={{ background: '#c4941a' }}
            onClick={() => navigate(`/partner/services/create`)}
          >
            <i className="bi bi-plus-lg me-1"></i>Add Service
          </button>
        </div>
      </div>

      <div className="app-content flex-column-fluid">
        <div className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <div className="d-flex align-items-center position-relative my-1">
                  <i className="bi bi-search position-absolute ms-3 text-muted"></i>
                  <input
                    type="text"
                    className="form-control form-control-solid w-400px ps-10"
                    placeholder="Search by title or description…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && getServices()}
                  />
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              {loading ? (
                <div className="text-center py-10"><Loader size={30} text="Loading" color="black" /></div>
              ) : services.length === 0 ? (
                <div className="text-center py-10 text-muted">
                  <i className="bi bi-grid fs-3x mb-3 d-block"></i>
                  No services found
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-hover mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                        <th style={{ paddingLeft: 20 }}>Image</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Price</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th style={{ paddingRight: 20 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {services.map((svc) => (
                        <tr key={svc._id}>
                          <td style={{ paddingLeft: 20 }}>
                            {svc.image
                              ? <img src={svc.image} alt={svc.title} className="rounded" style={{ width: 44, height: 44, objectFit: 'cover' }} />
                              : <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}><i className="bi bi-image text-muted"></i></div>
                            }
                          </td>
                          <td className="fw-bold text-dark">{svc.title}</td>
                          <td className="text-muted" style={{ maxWidth: 220 }}>
                            <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {svc.description || '—'}
                            </span>
                          </td>
                          <td>{svc.price != null ? <>AED {svc.price} <span className="text-muted fw-normal">/ {svc.price_type === 'per_hour' ? 'hour' : svc.price_type === 'per_item' ? 'item' : 'day'}</span></> : '—'}</td>
                          <td>
                            <span className={`badge text-dark bg-light-${svc.service_type === 'package' ? 'info' : 'primary'} text-capitalize`}>
                              {svc.service_type || 'item'}
                            </span>
                            {svc.service_type === 'item' && svc.max_quantity != null && (
                              <span className="text-muted fw-normal ms-1">(max {svc.max_quantity})</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge bg-${svc.status ? 'success' : 'secondary'}`}>
                              {svc.status ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ paddingRight: 20 }}>
                            <button
                              className="btn btn-icon btn-light-primary btn-sm me-1"
                              onClick={() => navigate(`/partner/services/edit/${svc._id}`)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil-fill fs-6"></i>
                            </button>
                            <button
                              className="btn btn-icon btn-light-danger btn-sm"
                              onClick={() => handleDelete(svc._id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash-fill fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerServices;
